import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../../services/api';
import { confirmAction } from '../../utils/confirm';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minWidth: 280,
    borderColor: state.isFocused ? '#3949ab' : '#e0e0e0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(26, 35, 126, 0.1)' : 'none',
    '&:hover': { borderColor: '#3949ab' },
    fontSize: 14,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#1a237e' : state.isFocused ? '#e8eaf6' : 'white',
  }),
  placeholder: (base) => ({ ...base, color: '#9e9e9e' }),
};

function CourseBOSAllocationTab({ tenantId }) {
  const [courses, setCourses] = useState([]);
  const [bosList, setBOSList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBOS, setSelectedBOS] = useState(null);
  const [filterBOS, setFilterBOS] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState(new Set());
  const [allocating, setAllocating] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending | allocated

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const [allocRes, bosRes] = await Promise.all([
        api.get(`/bos/allocations/${tenantId}`),
        api.get(`/bos/dropdown/${tenantId}`),
      ]);
      if (allocRes.data.success) setCourses(allocRes.data.data);
      if (bosRes.data.success) setBOSList(bosRes.data.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  // BOS options for react-select
  const bosOptions = useMemo(() =>
    bosList.map(b => ({ value: b.BOSID, label: b.BOSName })),
    [bosList]
  );

  // Filter BOS options (includes "All BOS" reset)
  const filterBOSOptions = useMemo(() => [
    ...bosList.map(b => ({ value: b.BOSID, label: b.BOSName })),
  ], [bosList]);

  // Filter and search
  const filteredCourses = useMemo(() => {
    let list = courses;

    // Search
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(c =>
        c.CourseCode.toLowerCase().includes(s) ||
        c.CourseName.toLowerCase().includes(s)
      );
    }

    // Status filter
    if (filter === 'allocated') {
      list = list.filter(c => c.BOSID);
    } else if (filter === 'pending') {
      list = list.filter(c => !c.BOSID);
    }

    // BOS filter
    if (filterBOS) {
      list = list.filter(c => c.BOSID === filterBOS.value);
    }

    return list;
  }, [courses, search, filter, filterBOS]);

  // Stats
  const stats = useMemo(() => {
    const total = courses.length;
    const allocated = courses.filter(c => c.BOSID).length;
    return { total, allocated, pending: total - allocated };
  }, [courses]);

  const toggleCourse = (courseId) => {
    setSelectedCourses(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedCourses.size === filteredCourses.length) {
      setSelectedCourses(new Set());
    } else {
      setSelectedCourses(new Set(filteredCourses.map(c => c.CourseID)));
    }
  };

  const handleAllocate = async () => {
    if (!selectedBOS) return toast.error('Please select a BOS from the dropdown');
    if (selectedCourses.size === 0) return toast.error('Please select at least one course');

    const confirmed = await confirmAction(
      'Allocate BOS?',
      `Allocate "${selectedBOS.label}" to ${selectedCourses.size} selected course(s)? This will replace any existing BOS allocation for these courses.`,
      { confirmText: 'Yes, allocate', confirmColor: '#2e7d32', icon: 'question' }
    );
    if (!confirmed) return;

    setAllocating(true);
    try {
      const res = await api.post('/bos/allocate', {
        tenantId,
        bosId: selectedBOS.value,
        courseIds: Array.from(selectedCourses),
      });
      toast.success(res.data.message);
      setSelectedCourses(new Set());
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Allocation failed');
    } finally {
      setAllocating(false);
    }
  };

  const handleDeallocate = async (allocationId, courseName) => {
    const confirmed = await confirmAction(
      'Remove Allocation?',
      `Remove BOS allocation from "${courseName}"?`,
      { confirmText: 'Yes, remove', confirmColor: '#c62828', icon: 'warning' }
    );
    if (!confirmed) return;
    try {
      await api.delete(`/bos/allocate/${allocationId}`);
      toast.success('Allocation removed');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('qposm_token');
      const res = await fetch(`${API_BASE}/bos/export/${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'BOS_Allocations_Export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>
          Course BOS Allocation
          <span style={{ fontWeight: 400, fontSize: 14, color: '#616161', marginLeft: 8 }}>
            ({stats.allocated} allocated / {stats.total} total)
          </span>
        </h3>
        <button className="btn btn-outline btn-sm" onClick={handleExport} title="Export to Excel">Export</button>
      </div>

      {/* Allocation toolbar */}
      <div className="alloc-toolbar">
        <div style={{ flex: '0 0 280px' }}>
          <Select
            options={bosOptions}
            value={selectedBOS}
            onChange={setSelectedBOS}
            placeholder="Search & select BOS to allocate..."
            isClearable
            isSearchable
            styles={selectStyles}
            noOptionsMessage={() => 'No BOS found'}
          />
        </div>
        <button
          className="btn btn-add"
          onClick={handleAllocate}
          disabled={allocating || !selectedBOS || selectedCourses.size === 0}
        >
          {allocating ? 'Allocating...' : `Allocate (${selectedCourses.size})`}
        </button>
      </div>

      {/* Filters */}
      <div className="user-filters" style={{ marginTop: 12 }}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Search by course code or name..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select
            options={filterBOSOptions}
            value={filterBOS}
            onChange={(val) => { setFilterBOS(val); if (val) setFilter('allocated'); }}
            placeholder="Filter by BOS..."
            isClearable
            isSearchable
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...selectStyles.control(base, state),
                minWidth: 220,
                minHeight: 38,
                fontSize: 13,
              }),
              option: (base, state) => ({
                ...selectStyles.option(base, state),
                fontSize: 13,
              }),
            }}
            noOptionsMessage={() => 'No BOS found'}
          />
        </div>
        <div className="alloc-filter-pills">
          <button className={`alloc-pill ${filter === 'all' && !filterBOS ? 'active' : ''}`} onClick={() => { setFilter('all'); setFilterBOS(null); }}>
            All <span className="alloc-pill-count">{stats.total}</span>
          </button>
          <button className={`alloc-pill alloc-pill-pending ${filter === 'pending' ? 'active' : ''}`} onClick={() => { setFilter('pending'); setFilterBOS(null); }}>
            Pending <span className="alloc-pill-count">{stats.pending}</span>
          </button>
          <button className={`alloc-pill alloc-pill-allocated ${filter === 'allocated' && !filterBOS ? 'active' : ''}`} onClick={() => { setFilter('allocated'); setFilterBOS(null); }}>
            Allocated <span className="alloc-pill-count">{stats.allocated}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : filteredCourses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          {courses.length === 0 ? 'No courses found. Add courses in Course Management first.' : 'No courses match your filter.'}
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th style={{ width: 44 }}>
                <input
                  type="checkbox"
                  checked={selectedCourses.size === filteredCourses.length && filteredCourses.length > 0}
                  onChange={toggleAll}
                  title="Select all"
                  style={{ width: 16, height: 16, accentColor: '#1a237e' }}
                />
              </th>
              <th>Course Code</th>
              <th>Course Name</th>
              <th>Credits</th>
              <th>BOS</th>
              <th>Allocated By</th>
              <th>Allocated On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCourses.map((c) => (
              <tr key={c.CourseID} className={selectedCourses.has(c.CourseID) ? 'row-selected' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedCourses.has(c.CourseID)}
                    onChange={() => toggleCourse(c.CourseID)}
                    style={{ width: 16, height: 16, accentColor: '#1a237e' }}
                  />
                </td>
                <td><strong>{c.CourseCode}</strong></td>
                <td>{c.CourseName}</td>
                <td style={{ textAlign: 'center' }}>{c.Credits}</td>
                <td>
                  {c.BOSName ? (
                    <span className="badge badge-provider">{c.BOSName}</span>
                  ) : (
                    <span className="badge badge-warning">Pending</span>
                  )}
                </td>
                <td style={{ color: '#616161', fontSize: 13 }}>{c.AllocatedByName || '-'}</td>
                <td style={{ color: '#616161', fontSize: 13 }}>{formatDate(c.AllocatedAt)}</td>
                <td>
                  {c.AllocationID && (
                    <button
                      className="action-btn action-delete"
                      title="Remove allocation"
                      onClick={() => handleDeallocate(c.AllocationID, c.CourseName)}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default CourseBOSAllocationTab;
